import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

// Assets
import purchaseIcon from "../../assets/down-arrow-blue.png";
import sellIcon from "../../assets/up-arrow-green.png";
import stockIcon from "../../assets/stock-icon-2.png";

// Utils
import { formatDate } from "../../utils/toDate";
import { UserAuth } from "../../utils/AuthContext";

// Components
import RoundedTable from "../../components/table-component/RoundedTable";
import PageWrapper from "../../components/PageWrapper";
import InformationBox from "../../components/InformationBox";
import MultipleSearchFilter from "../../components/MultipleSearchFilter";
import BalanceBoxes from "../../components/BalanceBoxes";
import Loader from "../../components/Loader";
import { TABLE_CELL_TYPES } from "../attendance-system/EmployeesAttendanceTable";
import PageHeadline from "../../components/PageHeadline";
import CopyButton from "../../components/CopyButton";
import { getProductById } from "../../services/productServices";

const ProductPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  // HOOKS
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);

  // STATES
  const [loading, setLoading] = useState(false);
  const [productData, setProductData] = useState([]);
  const [productInfo, setProductInfo] = useState({
    name: "",
    amountInStock: 0,
    averageCost: 0,
    unit: "meter",
  });
  const [selectedFilter, setSelectedFilter] = useState({
    title: t("status"),
    type: "dropdown",
    dropdown: {
      data: [
        { title: t("sell"), value: "SELL" },
        { title: t("purchase"), value: "PURCHASE" },
        { title: t("instock"), value: "INSTOCK" },
        { title: t("selectAll"), value: "" },
      ],
    },
  });
  const [searchQuery, setSearchQuery] = useState({
    title: t("selectAll"),
    value: "",
  });
  const [selectedOrderStatus, setSelectedOrderStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  // STATIC DATA
  const titles = [
    { value: t("Date"), type: TABLE_CELL_TYPES.DATE },
    { value: t("quantity"), type: TABLE_CELL_TYPES.TEXT },
    { value: t("singlePrice"), type: TABLE_CELL_TYPES.MONEY },
    { value: t("totalAmount"), type: TABLE_CELL_TYPES.MONEY },
    { value: t("status"), type: TABLE_CELL_TYPES.TEXT },
  ];

  const filterOptions = [
    {
      title: t("status"),
      type: "dropdown",
      dropdown: {
        data: [
          { title: t("sell"), value: "SELL" },
          { title: t("purchase"), value: "PURCHASE" },
          { title: t("instock"), value: "INSTOCK" },
          { title: t("selectAll"), value: "" },
        ],
      },
    },
  ];

  // ENDPOINTS
  const getProductData = async (filters = {}) => {
    setLoading(true);
    try {
      const type = searchParams.get("type");
      const response = await getProductById(id, type);
      const data = response.data;

      // Set product info from the main product data
      setProductInfo({
        name: data.name || "",
        size: data.size || "",
        color: data.color || "",
        amountInStock: data.amountInStock || 0,
        averageCost: data.averagePrice || data.price || 0,
        unit: data.unitOfMeasure?.toLowerCase() || "piece",
      });

      // Process product transactions
      let transactions = data.productTransaction || [];

      // Apply filters if any
      if (filters.orderType && filters.orderType.value) {
        transactions = transactions.filter(
          (transaction) =>
            transaction.transactionType === filters.orderType.value,
        );
      }

      // Apply date filters
      if (filters.startDate) {
        transactions = transactions.filter(
          (transaction) =>
            new Date(transaction.date) >= new Date(filters.startDate),
        );
      }
      if (filters.endDate) {
        transactions = transactions.filter(
          (transaction) =>
            new Date(transaction.date) <= new Date(filters.endDate),
        );
      }

      // For now, we'll handle pagination client-side since the API returns all transactions
      // In a real scenario, you might want to implement server-side pagination
      const itemsPerPage = 10;
      const totalRecords = transactions.length;
      const totalPages = Math.ceil(totalRecords / itemsPerPage);
      const startIndex = (pagination.currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedTransactions = transactions.slice(startIndex, endIndex);

      setPagination({
        currentPage: pagination.currentPage,
        totalPages: totalPages,
        totalRecords: totalRecords,
      });

      // Format data for the table
      const formatedData = paginatedTransactions.map((transaction) => {
        return [
          {
            value: formatDate(transaction.date),
            type: TABLE_CELL_TYPES.DATE,
          },
          {
            value: `${transaction.quantity || 0} ${t(
              data.unitOfMeasure?.toLowerCase() || "piece",
            )}`,
            type: TABLE_CELL_TYPES.TEXT,
          },
          {
            value: `${transaction.price || 0} ${t("IQD")}`,
            type: TABLE_CELL_TYPES.TEXT,
          },
          {
            value: `${transaction.price * transaction.quantity || 0} ${t("IQD")}`,
            type: TABLE_CELL_TYPES.TEXT,
          },
          {
            value: (
              <div className="u-max-width_100 u-width_full-width u-margin-center o-row o-row--horizontal-align_space-between o-row--vertical-align_center">
                <p>{t(transaction.transactionType.toLowerCase())}</p>
                <img
                  className="u-width-specific_20"
                  src={
                    transaction.transactionType === "PURCHASE"
                      ? purchaseIcon
                      : transaction.transactionType === "INSTOCK"
                        ? stockIcon
                        : sellIcon
                  }
                />
              </div>
            ),
            type: TABLE_CELL_TYPES.ELEMENT,
          },
        ];
      });

      setProductData(formatedData);
    } catch (error) {
      console.error("Error fetching product data", error);
      setProductData([]);
    } finally {
      setLoading(false);
    }
  };

  // HANDLERS
  const handleSearch = () => {
    const filters = {
      orderType: searchQuery,
    };

    if (startDate) {
      filters.startDate = startDate;
    }
    if (endDate) {
      filters.endDate = endDate;
    }

    setPagination((prev) => ({ ...prev, currentPage: 1 }));

    getProductData(filters);
  };

  // SIDE EFFECTS
  useEffect(() => {
    if (id) {
      getProductData();
    }
  }, [id, selectedOrderStatus, pagination.currentPage]);

  return (
    <PageWrapper withBackButton>
      <div className="u-padding u-padding--bottom_700">
        <PageHeadline
          title={t("productPage")}
          classList="u-margin--top_700 u-margin--bottom_400"
        />

        {/* Product Name Display */}
        <div className="o-row o-row--gap-size_300">
          {productInfo.name && (
            <div className="o-row o-row--gap-size_200 o-row--vertical-align_center u-margin--bottom_400">
              <CopyButton text={productInfo.name} />
              <span className="t-bold_semi-bold">
                {t("productName")}: {productInfo.name}
              </span>
            </div>
          )}

          {productInfo.size && (
            <div className="o-row o-row--gap-size_200 o-row--vertical-align_center u-margin--bottom_400">
              <CopyButton text={productInfo.size} />
              <span className="t-bold_semi-bold">
                {t("size")}: {productInfo.size}
              </span>
            </div>
          )}

          {productInfo.color && (
            <div className="o-row o-row--gap-size_200 o-row--vertical-align_center u-margin--bottom_400">
              <CopyButton text={productInfo.color} />
              <span className="t-bold_semi-bold">
                {t("color")}: {productInfo.color}
              </span>
            </div>
          )}
        </div>

        <div className="u-margin u-margin--bottom_400">
          <MultipleSearchFilter
            options={filterOptions}
            selected={selectedFilter}
            setSelected={setSelectedFilter}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            handleSearch={handleSearch}
          />
        </div>

        {loading && <Loader />}

        {productData.length && !loading ? (
          <RoundedTable
            titles={titles}
            rows={productData}
            setPagination={setPagination}
            pagination={pagination}
          />
        ) : !loading ? (
          <InformationBox>{t("noTransactions")}</InformationBox>
        ) : (
          <></>
        )}

        <div>
          <BalanceBoxes
            boxes={[
              {
                title: "amountInStock",
                value: productInfo.amountInStock + " " + t(productInfo.unit),
              },
              {
                title: "averageCost",
                value: productInfo.averageCost.toFixed(2) + " " + t("IQD"),
              },
            ]}
          />
        </div>
      </div>
    </PageWrapper>
  );
};

export default ProductPage;
